import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { map, Observable, of, take, tap } from 'rxjs';
import {
  LicenseBatchDto,
  LicenseBatchRequestDto,
  LicenseProjection,
  ScopeListDto,
} from '../models/license.model';
import { LicenseBatchService } from './license-batch.service';

type LicenseBatchState = {
  batches: LicenseBatchDto[];
  scopes: LicenseBatchDto['scope'][];
  selectedBatch: LicenseBatchDto | null;
  loading: boolean;
  saving: boolean;
  error: unknown | null;
};

const DEFAULT_LIST_PROJECTION: LicenseProjection = 'BRIEF';
const DEFAULT_SINGLE_BATCH_PROJECTION: LicenseProjection = 'FULL';

const initialState: LicenseBatchState = {
  batches: [],
  scopes: [],
  selectedBatch: null,
  loading: false,
  saving: false,
  error: null,
};

export interface GetLicenseBatchPayload {
  batchId: string;
  projection?: LicenseProjection;
  forceRefresh?: boolean;
}

export interface GetLicenseBatchesByCompanyPayload {
  companyId: string;
  projection?: LicenseProjection;
}

export interface UpdateLicenseBatchPayload {
  batchId: string;
  payload: LicenseBatchRequestDto;
}

export const LicenseBatchStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasBatches: computed(() => store.batches().length > 0),
    hasScopes: computed(() => store.scopes().length > 0),
    busy: computed(() => store.loading() || store.saving()),
  })),
  withMethods((store, licenseBatchService = inject(LicenseBatchService)) => {
    const normalizeBatchProjection = (
      batch: LicenseBatchDto,
      projection: LicenseProjection,
    ): LicenseBatchDto => ({
      ...batch,
      projection: batch.projection ?? projection,
    });

    const normalizeBatchesProjection = (
      batches: LicenseBatchDto[],
      projection: LicenseProjection,
    ): LicenseBatchDto[] =>
      batches.map((batch) => normalizeBatchProjection(batch, projection));

    const upsertBatch = (
      batches: LicenseBatchDto[],
      batch: LicenseBatchDto,
      fallbackBatchId?: string,
    ): LicenseBatchDto[] => {
      const resolvedBatchId = batch.id ?? fallbackBatchId ?? '';
      if (!resolvedBatchId) {
        return [...batches, batch];
      }

      let foundMatch = false;
      const updatedBatches = batches.map((existingBatch) => {
        if (existingBatch.id !== resolvedBatchId) {
          return existingBatch;
        }
        foundMatch = true;
        return { ...existingBatch, ...batch, id: resolvedBatchId };
      });

      if (foundMatch) {
        return updatedBatches;
      }

      return [...updatedBatches, { ...batch, id: resolvedBatchId }];
    };

    const fetchBatch$ = ({
      batchId,
      projection = DEFAULT_SINGLE_BATCH_PROJECTION,
    }: GetLicenseBatchPayload): Observable<LicenseBatchDto> => {
      patchState(store, { loading: true, error: null });
      return licenseBatchService.getById(batchId, projection).pipe(
        take(1),
        map((batch) => normalizeBatchProjection(batch, projection)),
        tap({
          next: (batch) => {
            patchState(store, {
              batches: upsertBatch(store.batches(), batch, batchId),
              selectedBatch: batch,
              loading: false,
            });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        }),
      );
    };

    const normalizeScopes = (
      response: ScopeListDto | null | undefined,
    ): LicenseBatchDto['scope'][] =>
      (response?.scopes ?? []).filter(
        (scope): scope is LicenseBatchDto['scope'] =>
          !!scope?.id?.trim() && !!scope?.name?.trim(),
      );

    return {
      getAllBatches(
        projection: LicenseProjection = DEFAULT_LIST_PROJECTION,
      ): void {
        patchState(store, { loading: true, error: null });
        licenseBatchService
          .getAll(projection)
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              patchState(store, {
                batches: normalizeBatchesProjection(
                  result.list ?? [],
                  projection,
                ),
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      getAllBatchesByCompany({
        companyId,
        projection = DEFAULT_LIST_PROJECTION,
      }: GetLicenseBatchesByCompanyPayload): void {
        patchState(store, { loading: true, error: null });
        licenseBatchService
          .getAllByCurrentCompanyId(projection)
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              patchState(store, {
                batches: normalizeBatchesProjection(
                  result.list ?? [],
                  projection,
                ),
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      getBatch(payload: GetLicenseBatchPayload): void {
        fetchBatch$(payload).subscribe();
      },

      getScopes(): void {
        patchState(store, { loading: true, error: null });
        licenseBatchService
          .getScopes()
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              patchState(store, {
                scopes: normalizeScopes(response),
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      getBatchFromStoreOrFetch({
        batchId,
        projection = DEFAULT_SINGLE_BATCH_PROJECTION,
        forceRefresh = false,
      }: GetLicenseBatchPayload): Observable<LicenseBatchDto> {
        if (forceRefresh) {
          return fetchBatch$({ batchId, projection });
        }

        const cachedBatch =
          store
            .batches()
            .find(
              (batch) =>
                batch.id === batchId && batch.projection === projection,
            ) ?? null;

        if (cachedBatch) {
          patchState(store, { error: null, selectedBatch: cachedBatch });
          return of(cachedBatch);
        }

        return fetchBatch$({ batchId, projection });
      },

      createBatch(payload: LicenseBatchRequestDto): void {
        patchState(store, { saving: true, error: null });
        licenseBatchService
          .create(payload)
          .pipe(take(1))
          .subscribe({
            next: (batch) => {
              const normalizedBatch = normalizeBatchProjection(
                batch,
                batch.projection ?? DEFAULT_SINGLE_BATCH_PROJECTION,
              );
              patchState(store, {
                batches: upsertBatch(store.batches(), normalizedBatch),
                selectedBatch: normalizedBatch,
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      updateBatch({ batchId, payload }: UpdateLicenseBatchPayload): void {
        patchState(store, { saving: true, error: null });
        licenseBatchService
          .update(batchId, payload)
          .pipe(take(1))
          .subscribe({
            next: (batch) => {
              const normalizedBatch = normalizeBatchProjection(
                batch,
                batch.projection ?? DEFAULT_SINGLE_BATCH_PROJECTION,
              );
              patchState(store, {
                batches: upsertBatch(store.batches(), normalizedBatch, batchId),
                selectedBatch: normalizedBatch,
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      deleteBatch(batchId: string): void {
        patchState(store, { saving: true, error: null });
        licenseBatchService
          .delete(batchId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                batches: store
                  .batches()
                  .filter((batch) => batch.id !== batchId),
                selectedBatch:
                  store.selectedBatch()?.id === batchId
                    ? null
                    : store.selectedBatch(),
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      clearError(): void {
        patchState(store, { error: null });
      },

      reset(): void {
        patchState(store, initialState);
      },
    };
  }),
);
